import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { useTheme } from "next-themes";
import {
  LayoutGrid, Radar, Newspaper, BookOpen, BriefcaseBusiness, RefreshCw, Users, Search, LogOut,
  PanelLeftClose, PanelLeftOpen, Sun, Moon, Menu, CalendarDays,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useMe } from "@/lib/me";
import { can, AREA_PERMISSION } from "@/lib/constants";
import { RoleBadge } from "@/components/app/shared";
import CommandPalette from "@/components/app/CommandPalette";
import { cn } from "@/lib/utils";

export const NAV = [
  { to: "/", label: "Centro de Mando", icon: LayoutGrid, id: "centro-de-mando", perm: null, n: "01" },
  { to: "/radar", label: "Radar AI", icon: Radar, id: "radar", perm: "access.radar", n: "02" },
  { to: "/editorial", label: "Mesa Editorial", icon: Newspaper, id: "editorial", perm: "access.editorial", n: "03" },
  { to: "/flatplan", label: "Flatplan Libro Anual", icon: BookOpen, id: "flatplan", perm: "access.flatplan", n: "04" },
  { to: "/crm", label: "CRM Comercial", icon: BriefcaseBusiness, id: "crm", perm: "access.crm", n: "05" },
  { to: "/integraciones", label: "Webflow & Integraciones", icon: RefreshCw, id: "integraciones", perm: "access.integrations", n: "06" },
  { to: "/equipo", label: "Equipo & Ajustes", icon: Users, id: "equipo", perm: "access.team", n: "07" },
  { to: "/agenda", label: "Agenda & Tareas", icon: CalendarDays, id: "agenda", perm: null, n: "08" },
];

function SidebarBody({ collapsed, setCollapsed, onSearch, onNavigate }) {
  const me = useMe();
  const { signOut } = useClerk();
  const { resolvedTheme, setTheme } = useTheme();
  const qc = useQueryClient();
  const items = NAV.filter((n) => !n.perm || can(me, n.perm));
  return (
    <div className="flex h-full flex-col">
      <div className={cn("flex items-start justify-between gap-2 px-4 pb-4 pt-5", collapsed && "flex-col items-center px-2")}>
        <div className={cn(collapsed && "text-center")}>
          <div className="font-display text-[28px] leading-none tracking-tight" data-testid="sidebar-logo">
            {collapsed ? "MH" : "Mediana Hub"}
          </div>
          {!collapsed && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded border border-border bg-secondary px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="env-tag">
              <span className="h-1.5 w-1.5 rounded-full dot-ok" /> Production / v1.0
            </div>
          )}
        </div>
        {setCollapsed && (
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-button" aria-label="Colapsar barra lateral">
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </Button>
        )}
      </div>
      <div className={cn("px-3", collapsed && "px-2")}>
        <button
          onClick={onSearch}
          data-testid="cmdk-open-button"
          className="flex h-9 w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent"
        >
          <Search className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <>
              <span className="flex-1 text-left">Buscar…</span>
              <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
            </>
          )}
        </button>
      </div>
      <nav className="mt-5 flex-1 space-y-0.5 overflow-y-auto px-3">
        {!collapsed && <div className="kicker mb-2 px-2">Módulos</div>}
        {items.map((n) => {
          const link = (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={onNavigate}
              data-testid={`sidebar-nav-${n.id}`}
              className={({ isActive }) =>
                cn(
                  "group flex h-9 items-center gap-3 rounded-md px-2 text-[13px] transition-colors",
                  isActive ? "bg-primary text-primary-foreground font-medium" : "text-foreground/80 hover:bg-accent hover:text-foreground",
                  collapsed && "justify-center px-0"
                )
              }
            >
              <n.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{n.label}</span>
                  <span className="font-mono text-[10px] opacity-50">{n.n}</span>
                </>
              )}
            </NavLink>
          );
          return collapsed ? (
            <Tooltip key={n.to}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{n.label}</TooltipContent>
            </Tooltip>
          ) : (
            link
          );
        })}
      </nav>
      <div className={cn("border-t border-border p-3", collapsed && "px-2")}>
        {!collapsed ? (
          <>
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-mono text-[11px] text-primary-foreground">
                {(me.name || me.email).split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium">{me.name}</div>
                <div className="truncate font-mono text-[10.5px] text-muted-foreground" data-testid="sidebar-user-email">{me.email}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <RoleBadge role={me.role} />
              <div className="flex items-center gap-1.5">
                <Sun className="h-3.5 w-3.5 text-muted-foreground" />
                <Switch checked={resolvedTheme === "dark"} onCheckedChange={(v) => setTheme(v ? "dark" : "light")} data-testid="theme-toggle-switch" aria-label="Cambiar tema" />
                <Moon className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full justify-start gap-2"
              data-testid="sign-out-button"
              onClick={() => {
                qc.clear();
                signOut({ redirectUrl: "/sign-in" });
              }}
            >
              <LogOut className="h-3.5 w-3.5" /> Cerrar sesión
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")} data-testid="theme-toggle-switch" aria-label="Cambiar tema">
              {resolvedTheme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => signOut({ redirectUrl: "/sign-in" })} data-testid="sign-out-button" aria-label="Cerrar sesión">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("mh-collapsed") === "1");
  const [cmdOpen, setCmdOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("mh-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        data-testid="sidebar"
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 border-r border-border bg-card transition-[width] duration-200 lg:block",
          collapsed ? "w-[72px]" : "w-[264px]"
        )}
      >
        <SidebarBody collapsed={collapsed} setCollapsed={setCollapsed} onSearch={() => setCmdOpen(true)} />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[280px] bg-card p-0">
          <SheetTitle className="sr-only">Navegación</SheetTitle>
          <SidebarBody collapsed={false} onSearch={() => { setMobileOpen(false); setCmdOpen(true); }} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="min-w-0 flex-1">
        <div className="sticky top-0 z-30 flex h-12 items-center justify-between border-b border-border bg-background px-4 lg:hidden">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label="Abrir menú">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="font-display text-2xl" onClick={() => navigate("/")}>Mediana Hub</div>
          <Button variant="ghost" size="icon" onClick={() => setCmdOpen(true)} aria-label="Buscar">
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <main className="mx-auto w-full max-w-[1480px] px-4 py-8 md:px-8 md:py-10">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={cmdOpen} setOpen={setCmdOpen} />
    </div>
  );
}
